import { useEffect, useRef, useState } from "react";
import { Button } from "../../shared";
import { UserOnboarding } from "./User";
import { renderValidations } from "../../shared/Table/table.utils";
import validations from "./validations";
import useForm from "../../hooks/useForm";
import { CustomerOnboarding } from "./Customer";
import { useAuth, useQueryNavigation } from "../../hooks";
import { useNavigate } from "react-router-dom";
import useQuery from "../../hooks/useQuery";
import { toast } from "react-toastify";
import request from "../../utils/request";
import { REGISTRATION_HEADER_NAME } from "../../config/defaultSettings";
import {
  useActivateCustomerMutation,
  useRequestCustomerInviteMutation,
} from "../../redux/services/customers/customersApi";
import { useActivateAccountMutation } from "../../redux/services/account/accountApi";

const path = [
  { id: "namePassword", sort: 1, content: UserOnboarding },
  { id: "customer", sort: 2, content: CustomerOnboarding },
];

export const Onboarding = () => {
  const { setParam, getParam } = useQueryNavigation({ id: "onboarding" });
  const { setUser, setParentCompany } = useAuth();
  const navigate = useNavigate();
  const queries = useQuery();
  const token = queries.get("token");
  const inviteContextRef = useRef(null);

  if (!inviteContextRef.current) {
    const payload = (() => {
      if (!token) return {};

      try {
        const [, rawPayload] = token.split(".");
        if (!rawPayload) return {};

        return (
          JSON.parse(
            window.atob(rawPayload.replace(/-/g, "+").replace(/_/g, "/"))
          ) || {}
        );
      } catch {
        return {};
      }
    })();

    inviteContextRef.current = {
      token,
      customerId:
        queries.get("customer") ||
        payload?.parentCompany ||
        payload?.parentCompany?._id ||
        null,
      userId: queries.get("userId") || payload?.id || null,
    };
  }

  const inviteContext = inviteContextRef.current;
  const [currentUser, setCurrentUser] = useState({});
  const [companyData, setCompanyData] = useState({});
  const [inviteError, setInviteError] = useState("");
  const [inviteErrorCode, setInviteErrorCode] = useState("");
  const [inviteRequestMessage, setInviteRequestMessage] = useState("");
  const [completionMessage, setCompletionMessage] = useState("");
  function getInvitePayload(inviteToken) {
    if (!inviteToken) return {};

    try {
      const [, payload] = inviteToken.split(".");
      if (!payload) return {};

      const decoded = JSON.parse(
        window.atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
      );
      return decoded || {};
    } catch {
      return {};
    }
  }

  const invitePayload = getInvitePayload(token);
  const invitedEmail = invitePayload?.email;

  const [activateAccount, { isLoading: isActivateAccountLoading }] =
    useActivateAccountMutation();
  const [activateCustomer, { isLoading: isActivateCustomerLoading }] =
    useActivateCustomerMutation();
  const [requestCustomerInvite, { isLoading: isRequestingInvite }] =
    useRequestCustomerInviteMutation();

  const isLoading =
    isActivateAccountLoading ||
    isActivateCustomerLoading ||
    isRequestingInvite;

  function getCurrentPath() {
    return path.find((i) => i.id === getParam("onboarding"));
  }
  function getNextPath(currentId) {
    return path.find((i) => i.sort === currentId + 1);
  }
  const currentPath = getCurrentPath() || path[0];

  function buildInviteAuthHeaders(customerId) {
    if (!inviteContext?.token) {
      return {};
    }

    const headers = {
      [REGISTRATION_HEADER_NAME]: inviteContext.token,
    };

    if (customerId) {
      headers["x-parent-company"] = customerId;
      headers["customer-context"] = customerId;
    }

    return headers;
  }

  function setOnboardingStep(stepId) {
    const nextParams = {
      onboarding: stepId,
    };

    if (inviteContext?.token) {
      nextParams.token = inviteContext.token;
    }
    if (inviteContext?.customerId) {
      nextParams.customer = inviteContext.customerId;
    }
    if (inviteContext?.userId) {
      nextParams.userId = inviteContext.userId;
    }

    setParam(nextParams);
  }

  function getParentCompanyId(user) {
    const parentCompany = user?.parentCompany;
    if (!parentCompany) return null;

    return typeof parentCompany === "object"
      ? parentCompany._id || parentCompany.id || null
      : parentCompany;
  }

  function getCustomerFormData(customer) {
    const address = customer?.address || {};

    return {
      name: customer?.name || "",
      slug: customer?.slug || "",
      phone: customer?.phone || "",
      address: address?.address || "",
      city: address?.city || "",
      state: address?.state || "",
      zipcode: address?.zipcode || "",
      suite: address?.suite || "",
      country: address?.country || "",
      parentCompanyId: customer?._id || "",
    };
  }

  async function fetchParentCompany(parentCompanyId) {
    if (!parentCompanyId) {
      return null;
    }

    const response = await request(`/customers/${parentCompanyId}`, {
      method: "GET",
      params: {
        parentCompany: parentCompanyId,
        ...(inviteContext?.token ? { token: inviteContext.token } : {}),
      },
      headers: buildInviteAuthHeaders(parentCompanyId),
    });

    return response.data?.customer || null;
  }

  function finalizeOnboarding(user, parentCompanyId) {
    if (user) {
      setUser(user);
    }
    if (parentCompanyId) {
      setParentCompany(parentCompanyId);
    }
    const companyName = user?.parentCompany?.name || companyData?.name;
    setCompletionMessage(
      companyName
        ? `${companyName} is ready. Taking you to Attune Logic...`
        : "Your account is ready. Taking you to Attune Logic..."
    );
    setTimeout(() => {
      navigate("/");
    }, 1200);
  }

  async function handleRequestNewInvite() {
    const customerId = inviteContext?.customerId;
    if (!customerId) {
      navigate("/login");
      return;
    }

    try {
      const response = await requestCustomerInvite({ id: customerId }).unwrap();
      setInviteRequestMessage(
        response?.message ||
          "A fresh onboarding email is on the way. Please check your inbox."
      );
    } catch (error) {
      const message =
        error?.data?.message ||
        "We could not send a fresh invite right now. Please contact your account administrator.";
      toast(message, {
        type: "error",
        toastId: "request-customer-invite-error",
      });
    }
  }

  useEffect(() => {
    if (!token) {
      setInviteErrorCode("INVITE_MISSING");
      setInviteError("This activation link is invalid or missing a token.");
      return;
    }

    setOnboardingStep(currentPath.id || path[0].id);
  }, []);

  useEffect(() => {
    const customerId = inviteContext?.customerId;
    if (!inviteContext?.token || !customerId || companyData?.name) {
      return;
    }

    const loadInviteCompany = async () => {
      try {
        const response = await request(`/customers/${customerId}`, {
          method: "GET",
          params: {
            parentCompany: customerId,
            token: inviteContext.token,
          },
          headers: buildInviteAuthHeaders(customerId),
        });

        if (response.data?.customer) {
          setCompanyData(response.data.customer);
        }
      } catch (_error) {
        // Ignore prefill failures; the invite can still continue.
      }
    };

    loadInviteCompany();
  }, [companyData?.name, inviteContext?.customerId, inviteContext?.token]);

  const form = useForm({
    validations: renderValidations(validations[currentPath.id]),
  });

  async function handleActivateAccount() {
    const formData = {
      ...form.form,
    };
    let activated;

    try {
      activated = await activateAccount({ token, data: formData }).unwrap();
    } catch (error) {
      setInviteErrorCode(error?.data?.code || "");
      const message =
        error?.data?.message ||
        "This invite link is invalid, expired, or has already been used.";

      setInviteError(message);
      return null;
    }

    const user = activated.user;
    const parentCompanyId = getParentCompanyId(user);
    const customer = await fetchParentCompany(parentCompanyId);
    const requiresCustomerActivation =
      Boolean(user?.accountHolder) && !customer?.metadata?.onboarded;

    setCurrentUser(user);
    setCompanyData(customer || {});

    if (customer) {
      form.setFormInit(
        {
          ...getCustomerFormData(customer),
          firstName: form.form.firstName,
          lastName: form.form.lastName,
          password: form.form.password,
          confirm: form.form.confirm,
          strength: form.form.strength,
        },
        { reset: true }
      );
    }

    toast(activated.message, {
      type: activated.status,
      toastId: "userActivated",
    });

    return {
      user,
      parentCompanyId,
      requiresCustomerActivation,
    };
  }

  async function handleActivateCustomer() {
    const parentCompanyId =
      form.form.parentCompanyId || getParentCompanyId(currentUser);
    const formData = {
      name: form.form.name,
      phone: form.form.phone,
      slug: form.form.slug,
      address: {
        address: form.form.address,
        city: form.form.city,
        state: form.form.state,
        zipcode: form.form.zipcode,
        suite: form.form.suite,
        country: form.form.country,
      },
    };

    let activated;

    try {
      activated = await activateCustomer({
        parentCompany: parentCompanyId,
        data: formData,
      }).unwrap();
    } catch (error) {
      const message =
        error?.data?.message ||
        "We could not finish setting up your company. Please review your details and try again.";

      toast(message, {
        type: "error",
        toastId: "customer-activation-error",
      });
      return null;
    }

    toast(activated.message, {
      type: activated.status,
      toastId: "userActivated",
    });

    return { activated, parentCompanyId };
  }

  async function handleSubmit() {
    if (currentPath.id === "namePassword") {
      return handleActivateAccount();
    }

    if (currentPath.id === "customer") {
      return handleActivateCustomer();
    }

    return null;
  }

  async function handleNext() {
    const next = getNextPath(currentPath.sort);
    const submit = await handleSubmit();

    if (currentPath.id === "namePassword") {
      if (!submit?.user) {
        return;
      }

      if (submit.requiresCustomerActivation && next) {
        setOnboardingStep(next.id);
        return;
      }

      finalizeOnboarding(submit.user, submit.parentCompanyId);
      return;
    }

    if (currentPath.id === "customer") {
      if (submit?.activated) {
        finalizeOnboarding(currentUser, submit.parentCompanyId);
      }
    }
  }

  function handleForm(e) {
    const { value, id } = e;
    form.setFormInit({ ...form.form, [id]: value }, { setErrors: true });
  }

  const Content = currentPath.content;
  const title =
    currentPath.id === "namePassword"
      ? "Finish setting up your account"
      : "Complete your company setup";
  const subtitle =
    currentPath.id === "namePassword"
      ? invitedEmail
        ? companyData?.name
          ? `You were invited to join ${companyData.name} as ${invitedEmail}. Add your details to continue.`
          : `You were invited as ${invitedEmail}. Add your details to continue.`
        : "Add your details to continue."
      : companyData?.name
      ? `Review ${companyData.name} before getting started.`
      : "Review your company details before getting started.";
  const submitLabel =
    currentPath.id === "namePassword" ? "Continue" : "Finish setup";
  const stepLabel = `Step ${currentPath.sort} of ${path.length}`;
  const finalStepLabel = `Step ${path.length} of ${path.length}`;
  const canRequestInvite = Boolean(inviteContext?.customerId);
  const inviteState = {
    title:
      inviteErrorCode === "INVITE_EXPIRED"
        ? "Invite expired"
        : inviteErrorCode === "INVITE_ALREADY_USED"
        ? "Invite already used"
        : "Invite link unavailable",
    helper:
      inviteErrorCode === "INVITE_EXPIRED"
        ? "This invite timed out before setup was completed. Request a fresh email to continue onboarding."
        : inviteErrorCode === "INVITE_ALREADY_USED"
        ? "This invite has already been completed once. If you still need access, request a fresh email for the pending owner account."
        : "Contact your account administrator if you still need access.",
    actionLabel:
      inviteErrorCode === "INVITE_ALREADY_USED" || inviteErrorCode === "INVITE_EXPIRED"
        ? "Send a fresh invite"
        : "Request a new invite",
  };

  if (inviteError) {
    return (
      <div className="flex flex-col items-center justify-center my-auto pt-8 px-4 md:px-5 lg:px-24">
        <div className="w-full max-w-xl text-center space-y-4">
          <div className="text-sm font-medium text-primary-600 text-center">
            Invite issue
          </div>
          <div className="text-3xl font-bold">{inviteState.title}</div>
          <div className="text-gray-500">{inviteError}</div>
          <div className="text-sm text-gray-500">
            {inviteRequestMessage || inviteState.helper}
          </div>
          <div className="flex items-center justify-center gap-2 pt-4">
            <Button type="alternative" onClick={() => navigate("/login")}>
              Back to login
            </Button>
            <Button
              disabled={!canRequestInvite || Boolean(inviteRequestMessage)}
              loading={isRequestingInvite}
              onClick={handleRequestNewInvite}
            >
              {inviteRequestMessage ? "Invite requested" : inviteState.actionLabel}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (completionMessage) {
    return (
      <div className="flex flex-col items-center justify-center my-auto pt-8 px-4 md:px-5 lg:px-24">
        <div className="w-full max-w-xl text-center space-y-4">
          <div className="text-sm font-medium text-primary-600 text-center">
            {finalStepLabel}
          </div>
          <div className="text-3xl font-bold">Setup complete</div>
          <div className="text-gray-500">{completionMessage}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center md:justify-start my-auto pt-8 md:pt-0 px-1 md:px-5 lg:px-24">
      <div className="flex flex-col justify-center items-center md:w-full">
        <div className="text-sm font-medium text-primary-600 text-center mb-2">
          {stepLabel}
        </div>
        <div className="text-3xl font-bold text-center">{title}</div>
        <div className="text-gray-500 text-center">{subtitle}</div>
        <Content
          {...form}
          handleForm={handleForm}
          currentUser={currentUser}
          companyData={companyData}
        />
      </div>
      <div className="flex flex-col justify-center items-center">
        <div className="pt-10 w-full flex justify-end">
          <Button
            disabled={!form.isValid}
            loading={isLoading}
            onClick={handleNext}
          >
            {submitLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};
